import { useSuspenseQueryParams } from "hooks/useSuspenseQueryParams";
import { createContext, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";

import { useWasteFacility } from "./useWasteFacility";
import { Filter } from "./WasteFacilityFilters";

export type WasteFacilityState = {
  optimisticFilters: Partial<Filter>;
  deferredFilters: Partial<Filter>;
  isPending: boolean;
};

type WasteFacilityContextType = {
  wasteFacilityState: WasteFacilityState;
  setWasteFacilityState: (cb: (state: Partial<Filter>) => void) => void;
  /** The facility ID to use for waste-related queries (primary facility if colocated) */
  effectiveWasteFacilityId: string;
  /** The primary waste facility info if this facility is colocated, null otherwise */
  colocationWasteFacility: { id: string; name: string } | null | undefined;
};

const WasteFacilityStateContext = createContext<WasteFacilityContextType | undefined>(undefined);

export function useWasteFacilityState() {
  const context = useContext(WasteFacilityStateContext);
  if (!context) {
    throw new Error("useWasteFacilityState must be used within a WasteFacilityStateProvider");
  }
  return context;
}

export const WasteFacilityProvider = ({ children }: { children: React.ReactNode }) => {
  const { facilityId } = useParams<{ facilityId: string }>();
  const { data } = useWasteFacility(facilityId);
  const { optimisticFilters, deferredFilters, setFilters, isPending } = useSuspenseQueryParams();

  const wasteFacilityState = useMemo(
    () => ({ deferredFilters, optimisticFilters, isPending }),
    [deferredFilters, optimisticFilters, isPending]
  );

  // Use the primary facility ID for waste queries if this facility is colocated
  const effectiveWasteFacilityId = data.facility?.colocationWasteFacilityId ?? facilityId ?? "";

  const colocationWasteFacility = data.facility?.colocationWasteFacility;

  const value: WasteFacilityContextType = useMemo(
    () => ({
      wasteFacilityState,
      setWasteFacilityState: setFilters,
      effectiveWasteFacilityId,
      colocationWasteFacility,
    }),
    [wasteFacilityState, setFilters, effectiveWasteFacilityId, colocationWasteFacility]
  );

  return (
    <WasteFacilityStateContext.Provider value={value}>
      {children}
    </WasteFacilityStateContext.Provider>
  );
};
